import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Save, X } from 'lucide-react';

const emptyForm = () => ({
  companyId: '',
  companyName: '',
  email: '',
  address: '',
  primaryMobile: '',
  secondaryMobile: '',
});

function apiErrorMessage(res, json, fallback) {
  const msg = String(json?.message || '').trim();
  if (res.status === 404 && /route not found/i.test(msg)) {
    return (
      'The API has no /api/shipping-agents route (often an old Node server). ' +
      'Stop and restart the backend from the BACKEND folder (npm run dev or npm start), then refresh this page.'
    );
  }
  return msg || fallback;
}

/**
 * Full CRUD for shipping agents (directory). Used from Admin → Shipping agents tab.
 */
export default function AdminShippingAgentsPanel({ apiBase, authToken, isActive }) {
  const [agents, setAgents] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [showForm, setShowForm] = useState(false);

  const loadAgents = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    setLoadError('');
    try {
      const res = await fetch(`${apiBase}/api/shipping-agents`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && Array.isArray(json.data)) {
        setAgents(json.data);
      } else {
        setAgents([]);
        if (!res.ok) {
          console.error('Shipping agents load failed', json);
          setLoadError(apiErrorMessage(res, json, 'Could not load shipping agents.'));
        }
      }
    } catch (e) {
      console.error(e);
      setAgents([]);
      setLoadError(e.message || 'Could not reach the API. Check that the backend is running and REACT_APP_API_URL is correct.');
    } finally {
      setLoading(false);
    }
  }, [apiBase, authToken]);

  useEffect(() => {
    if (isActive && authToken) {
      loadAgents();
    }
  }, [isActive, authToken, loadAgents]);

  const startCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const startEdit = (a) => {
    setEditingId(String(a._id));
    setForm({
      companyId: a.companyId || '',
      companyName: a.companyName || '',
      email: a.email || '',
      address: a.address || '',
      primaryMobile: a.primaryMobile || '',
      secondaryMobile: a.secondaryMobile || '',
    });
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm());
  };

  const handleSave = async () => {
    if (!authToken) return;
    if (!form.companyName.trim() && !form.companyId.trim()) {
      alert('Company name or company ID is required.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        companyId: form.companyId.trim(),
        companyName: form.companyName.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        primaryMobile: form.primaryMobile.trim(),
        secondaryMobile: form.secondaryMobile.trim(),
      };
      const url = editingId
        ? `${apiBase}/api/shipping-agents/${editingId}`
        : `${apiBase}/api/shipping-agents`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(apiErrorMessage(res, json, 'Save failed'));
        return;
      }
      await loadAgents();
      cancelForm();
    } catch (e) {
      alert(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!authToken || !window.confirm('Delete this shipping agent from the directory?')) return;
    try {
      const res = await fetch(`${apiBase}/api/shipping-agents/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        alert(apiErrorMessage(res, j, 'Delete failed'));
        return;
      }
      await loadAgents();
      if (editingId === String(id)) cancelForm();
    } catch (e) {
      alert(e.message || 'Delete failed');
    }
  };

  return (
    <div>
      <div className="jet-card" style={{ marginBottom: '1rem' }}>
        <div className="jet-table-toolbar" style={{ flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <h2 className="jet-table-title" style={{ margin: 0 }}>
              Shipping agents
            </h2>
            <p style={{ margin: '0.35rem 0 0', fontSize: '0.875rem', color: 'var(--jet-muted)', maxWidth: '40rem' }}>
              Maintain the directory used on shipments. On each shipment you only pick an agent by name; full details are
              stored here.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button type="button" className="jet-btn jet-btn-primary" onClick={startCreate}>
              <Plus size={16} aria-hidden />
              Add agent
            </button>
            <button type="button" className="jet-btn jet-btn-secondary" onClick={loadAgents} disabled={loading}>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {loadError ? (
        <div
          className="jet-card"
          role="alert"
          style={{
            marginBottom: '1rem',
            border: '1px solid #fecaca',
            background: '#fef2f2',
            color: '#991b1b',
          }}
        >
          <strong style={{ display: 'block', marginBottom: '0.35rem' }}>Could not load shipping agents</strong>
          <p style={{ margin: 0, fontSize: '0.875rem', lineHeight: 1.45 }}>{loadError}</p>
        </div>
      ) : null}

      {showForm && (
        <div className="jet-card" style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>
              {editingId ? 'Edit shipping agent' : 'New shipping agent'}
            </h3>
            <button type="button" className="jet-btn jet-btn-secondary jet-btn-icon" onClick={cancelForm} aria-label="Close form">
              <X size={18} />
            </button>
          </div>
          <div className="jet-shipment-form-grid" style={{ marginTop: '1rem' }}>
            <div>
              <label className="jet-shipment-label">Company ID</label>
              <input
                className="form-input"
                value={form.companyId}
                onChange={(e) => setForm((f) => ({ ...f, companyId: e.target.value }))}
                placeholder="Company ID"
              />
            </div>
            <div>
              <label className="jet-shipment-label">Company name</label>
              <input
                className="form-input"
                value={form.companyName}
                onChange={(e) => setForm((f) => ({ ...f, companyName: e.target.value }))}
                placeholder="Company name"
              />
            </div>
            <div>
              <label className="jet-shipment-label">Email address</label>
              <input
                type="email"
                className="form-input"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="service@example.com"
              />
            </div>
          </div>
          <div className="jet-agent-registration-row2" style={{ marginTop: '1rem' }}>
            <div className="jet-agent-registration-address">
              <label className="jet-shipment-label">Company address</label>
              <textarea
                className="form-textarea"
                rows={3}
                value={form.address}
                onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                placeholder="Full company address"
              />
            </div>
            <div>
              <label className="jet-shipment-label">Primary mobile</label>
              <input
                className="form-input"
                value={form.primaryMobile}
                onChange={(e) => setForm((f) => ({ ...f, primaryMobile: e.target.value }))}
                placeholder="0771234567"
              />
            </div>
            <div>
              <label className="jet-shipment-label">Secondary mobile (optional)</label>
              <input
                className="form-input"
                value={form.secondaryMobile}
                onChange={(e) => setForm((f) => ({ ...f, secondaryMobile: e.target.value }))}
                placeholder="0712345678"
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
            <button type="button" className="jet-btn jet-btn-primary" onClick={handleSave} disabled={saving}>
              <Save size={16} aria-hidden />
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="jet-btn jet-btn-secondary" onClick={cancelForm}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="jet-card">
        {loading ? (
          <p style={{ color: 'var(--jet-muted)' }}>Loading…</p>
        ) : agents.length === 0 ? (
          <p style={{ color: 'var(--jet-muted)' }}>No shipping agents yet. Click Add agent to create one.</p>
        ) : (
          <div className="jet-table-wrap">
            <table className="jet-table">
              <thead>
                <tr>
                  <th>Company name</th>
                  <th>Company ID</th>
                  <th>Email</th>
                  <th>Primary mobile</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {agents.map((a) => (
                  <tr key={a._id}>
                    <td style={{ fontWeight: 600 }}>{a.companyName || '—'}</td>
                    <td>{a.companyId || '—'}</td>
                    <td>{a.email || '—'}</td>
                    <td>{a.primaryMobile || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          className="jet-btn jet-btn-secondary jet-btn-icon"
                          title="Edit"
                          onClick={() => startEdit(a)}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          className="jet-btn jet-btn-danger jet-btn-icon"
                          title="Delete"
                          onClick={() => handleDelete(a._id)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
